import { useState, useEffect, useCallback, useRef } from "react";
import { fetcher } from "../../../utils/fetcher";
import { useResync } from "../../../hooks/useRealtime";
import FlowTextInput from "../../FlowTextInput/FlowTextInput";
import ToolTip from "../../ToolTip/ToolTip";
import Spinner from "../../Spinner/Spinner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { withAlpha } from "../../../utils/colors";
import { getTextPreview } from "../../../utils/textPreview";
import EditItemDescription from "../../../pages/ToDoList/Modals/EditItemDescription/EditItemDescription";
import SpacerModal from "../../../pages/ToDoList/Modals/SpacerModal/SpacerModal";
import "./ListEditorModal.scss";
import { useTranslation } from "react-i18next";

// Sortable Item Component
function SortableListItem({ item, index, color, onToggle, onDelete, onEdit }) {
  const { i18n } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id || `item-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition,
    opacity: isDragging ? 0.5 : 1,
    borderLeftColor: color,
  };

  // Spacer: a titled, colored separator - can't be checked off
  if (item.isSpacer) {
    const spacerColor = item.color || color;
    return (
      <div
        ref={setNodeRef}
        style={{
          ...style,
          borderLeftColor: spacerColor,
          backgroundColor: withAlpha(spacerColor, 0.18),
        }}
        className="list-item list-item-spacer"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(index);
        }}
      >
        <div className="item-content">
          <span className="item-text">{item.text}</span>
        </div>
        <button
          className="item-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(index);
          }}
        >
          <span className="material-symbols-outlined">delete</span>
        </button>
        <div
          className="drag-handle"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          style={{ touchAction: "none" }}
        >
          <span className="material-symbols-outlined">drag_indicator</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`list-item ${item.completed ? "completed" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onEdit(index);
      }}
    >
      <input
        type="checkbox"
        className="item-checkbox"
        checked={item.completed}
        onChange={() => onToggle(index)}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="item-content">
        <span className="item-text">{item.text}</span>
        {item.description && getTextPreview(item.description) && (
          <span className="item-description">
            {getTextPreview(item.description)}
          </span>
        )}
      </div>
      {item.deadline && (
        <span className="item-deadline">
          <span className="material-symbols-outlined">calendar_month</span>
          {new Date(item.deadline).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
          })}
        </span>
      )}
      <button
        className="item-delete-btn"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(index);
        }}
      >
        <span className="material-symbols-outlined">delete</span>
      </button>
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        style={{ touchAction: "none" }}
      >
        <span className="material-symbols-outlined">drag_indicator</span>
      </div>
    </div>
  );
}

function ListEditorModal({ listId, onListUpdated }) {
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newItemText, setNewItemText] = useState("");
  const [addToTop, setAddToTop] = useState(false);
  // Id of the item being edited - the modal shows its edit view instead of
  // the list until it is saved or cancelled. Tracked by id (not index) so
  // a live update of the list while editing can't swap the item.
  const [editingId, setEditingId] = useState(null);
  const toolTipRef = useRef();
  const newItemInputRef = useRef();

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Fetch the list data
  const loadList = useCallback(
    (showLoading = true) => {
      if (!listId) {
        setIsLoading(false);
        return;
      }

      if (showLoading) setIsLoading(true);
      fetcher
        .get(`/api/todolists/${listId}`)
        .then((data) => {
          setList(data);
          // Load addToTop preference from localStorage
          const storageKey = `todolist-addToTop-${data._id}`;
          const savedPref = localStorage.getItem(storageKey);
          if (savedPref !== null) {
            setAddToTop(JSON.parse(savedPref));
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching list:", err);
          setIsLoading(false);
        });
    },
    [listId],
  );

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Reload when the list may have been changed on another device
  useResync(() => loadList(false));

  // Save list to server
  const saveList = useCallback(
    async (updatedList) => {
      if (!updatedList) return;

      try {
        const updatedData = await fetcher.put(
          `/api/todolists/${updatedList._id}`,
          {
            rev: updatedList.rev,
            title: updatedList.title,
            items: updatedList.items,
          },
        );

        if (onListUpdated) {
          onListUpdated(updatedData);
        }

        return updatedData;
      } catch (err) {
        console.error("Error updating list:", err);
      }
    },
    [onListUpdated],
  );

  const handleToggleAddPosition = (toTop) => {
    if (!list) return;
    setAddToTop(toTop);
    const storageKey = `todolist-addToTop-${list._id}`;
    localStorage.setItem(storageKey, JSON.stringify(toTop));
  };

  const showToolTip = (e, content) => {
    const triggerElement = e.currentTarget;
    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow: "top-right",
        content,
        triggerElement,
      });
    }
  };

  const handleAddItem = async () => {
    if (!list || !newItemText.trim()) return;

    let updatedItems;

    if (addToTop) {
      // Add to top: new item at index 0, shift others down
      const newItem = {
        text: newItemText.trim(),
        completed: false,
        sortOrder: 0,
      };
      updatedItems = [
        newItem,
        ...list.items.map((item, index) => ({
          ...item,
          sortOrder: index + 1,
        })),
      ];
    } else {
      // Add to bottom: new item at the end
      const newItem = {
        text: newItemText.trim(),
        completed: false,
        sortOrder: list.items.length,
      };
      updatedItems = [...list.items, newItem];
    }

    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);
    setNewItemText("");

    // Save to server
    const savedList = await saveList(updatedList);
    if (savedList) {
      setList(savedList);
    }
  };

  const handleToggleItem = async (index) => {
    if (!list) return;

    const updatedItems = list.items.map((item, i) =>
      i === index ? { ...item, completed: !item.completed } : item,
    );
    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);

    // Save to server
    await saveList(updatedList);
  };

  const handleDeleteItem = async (index) => {
    if (!list) return;

    const updatedItems = list.items.filter((_, i) => i !== index);
    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);

    // Save to server
    await saveList(updatedList);
  };

  // Save changes to the item being edited, then go back to the list
  const handleSaveEditedItem = async (changes) => {
    if (!list || !editingId) return;

    const updatedItems = list.items.map((item) =>
      item._id === editingId ? { ...item, ...changes } : item,
    );
    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);
    setEditingId(null);

    const savedList = await saveList(updatedList);
    if (savedList) {
      setList(savedList);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !list) {
      return;
    }

    const oldIndex = list.items.findIndex(
      (item, idx) => (item._id || `item-${idx}`) === active.id,
    );
    const newIndex = list.items.findIndex(
      (item, idx) => (item._id || `item-${idx}`) === over.id,
    );

    const newItems = arrayMove(list.items, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        sortOrder: index,
      }),
    );

    // Optimistically update the UI first
    const updatedList = { ...list, items: newItems };
    setList(updatedList);

    // Save to server
    const savedList = await saveList(updatedList);
    if (savedList) {
      setList(savedList);
    }
  };

  if (isLoading) {
    return (
      <div className="list-editor-modal">
        <Spinner />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="list-editor-modal">
        <div className="empty-state">{t("tasks.links.listNotFound")}</div>
      </div>
    );
  }

  const editingItem = editingId
    ? list.items.find((item) => item._id === editingId)
    : null;
  if (editingItem) {
    return (
      <div className="list-editor-modal">
        <div className="list-editor-edit-view">
          {editingItem.isSpacer ? (
            <SpacerModal
              spacer={editingItem}
              defaultColor={list.color}
              onSave={(text, color) => handleSaveEditedItem({ text, color })}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <EditItemDescription
              item={editingItem}
              color={list.color}
              onUpdate={(itemId, text, description) =>
                handleSaveEditedItem({ text, description })
              }
              onCancel={() => setEditingId(null)}
            />
          )}
        </div>
      </div>
    );
  }

  // Spacers only separate items - don't count them
  const countableItems = list.items.filter((item) => !item.isSpacer);
  const completedCount = countableItems.filter((item) => item.completed).length;
  const totalCount = countableItems.length;

  return (
    <div className="list-editor-modal">
      <ToolTip ref={toolTipRef} />

      <div className="list-stats">
        <span
          className={`list-type-badge ${list.type === "checklist" ? "type-checklist" : "type-todolist"}`}
        >
          {list.type === "checklist"
            ? t("tasks.links.checklistLabel")
            : t("tasks.links.todoListLabel")}
        </span>
        <span className="list-progress">
          {t("tasks.links.completedOfTotal", {
            completed: completedCount,
            total: totalCount,
          })}
        </span>
      </div>

      <div className="list-add-item">
        <FlowTextInput
          ref={newItemInputRef}
          placeholder={t("tasks.links.addNewItem")}
          value={newItemText}
          onChange={setNewItemText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleAddItem();
            }
          }}
          expandable={false}
          icon="add"
        />
        <button
          className={`todo-action-btn ${addToTop ? "active" : ""}`}
          onClick={() => handleToggleAddPosition(true)}
          onMouseEnter={(e) => showToolTip(e, t("tasks.links.addToTop"))}
        >
          <span className="material-symbols-outlined">vertical_align_top</span>
        </button>
        <button
          className={`todo-action-btn ${!addToTop ? "active" : ""}`}
          onClick={() => handleToggleAddPosition(false)}
          onMouseEnter={(e) => showToolTip(e, t("tasks.links.addToBottom"))}
        >
          <span className="material-symbols-outlined">
            vertical_align_bottom
          </span>
        </button>
      </div>

      <div
        className="list-items-container"
        onClick={() => newItemInputRef.current?.focus()}
      >
        {list.items.length === 0 ? (
          <div className="empty-items">{t("tasks.links.noItems")}</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={list.items.map((item, idx) => item._id || `item-${idx}`)}
              strategy={verticalListSortingStrategy}
            >
              {list.items.map((item, index) => (
                <SortableListItem
                  key={item._id || index}
                  item={item}
                  index={index}
                  color={list.color}
                  onToggle={handleToggleItem}
                  onDelete={handleDeleteItem}
                  onEdit={() => item._id && setEditingId(item._id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}

export default ListEditorModal;
